/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

// Needed for redux-saga es6 generator support
import '@babel/polyfill';
import 'whatwg-fetch';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import FontFaceObserver from 'fontfaceobserver';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import 'sanitize.css/sanitize.css';

// Import root app
import App from './components/pages/App';

import LanguageProvider from './components/pages/LanguageProvider';
import SomethingWentWrong from './components/pages/SomethingWentWrong';

/* eslint-disable import/no-unresolved, import/extensions */
import '!file-loader?name=[name].[ext]!./favicon.ico';
import 'file-loader?name=.htaccess!./.htaccess';
/* eslint-enable import/no-unresolved, import/extensions */

import { configureStore } from '@capillarytech/vulcan-react-sdk/utils'
import ConfigProvider from 'antd/lib/config-provider';
import { StyleProvider } from '@ant-design/cssinjs';
import { getCapThemeConfig, loadCapUI } from '@capillarytech/cap-ui-library/utils';

loadCapUI();
import initialState from './initialState';
import { translationMessages } from './i18n';
// add custom polyfills if not natively supported by some browsers
import './polyfills';
import { initialReducer } from './initialReducer'
import history from './utils/history';
// Defines window.getCapAuthData() at host bootstrap so MFE remotes can await the
// host's single auth call even if they ask before it resolves.
import './utils/capAuthBridge';
import initMfeCssScope from './utils/mfeCssScope';
import MfeGtmService from './services/MfeGtmService';

const openSansObserver = new FontFaceObserver('Roboto', {});

// When Roboto is loaded, add a font-family using Robotot to the body
openSansObserver
  .load()
  .then(() => {
    document.body.classList.add('fontLoaded');
  })
  .catch(err => {
    Bugsnag.notify(err);
    console.log(err);
  });

// Mark this page as MFE host so remote apps can detect MFE mode
window.MFE_HOST= true;

// Keep only the active remote's stylesheets enabled — remotes inject their own
// (version-drifting) cap-ui-library CSS into <head> and never remove it, so global
// rules from every visited remote otherwise pile up and clobber the current page.
initMfeCssScope(history);


// Create redux store with history
const store = configureStore(initialState, initialReducer, history);

// Initialise centralised GTM manager — listens for gtm:init from remote MFEs
MfeGtmService.init();
const MOUNT_NODE = document.getElementById('intouch-app');
const root = createRoot(MOUNT_NODE);
const bugSnagErrorCallback = event => {
  const { app: { releaseStage } = {} } = event || {};
  if (
    (releaseStage || '').includes('nightly') ||
    process.env.NODE_ENV !== 'production'
  ) {
    return false;
  }
  let userId;
  let userName;
  let userEmail;
  let orgId;
  let orgName;
  try {
    const { id, firstName, lastName, loginName, proxyOrgList } = JSON.parse(
      window.localStorage.getItem('user'),
    );
    orgId = window.localStorage.getItem('orgID');
    userId = id;
    userName = `${firstName} ${lastName}`;
    userEmail = loginName;
    orgName = proxyOrgList.find(({ orgID }) => orgID === Number(orgId)).orgName;
  } catch (error) {
    userId = '';
    userName = '';
    userEmail = '';
    orgId = '';
    orgName = '';
  }
  event.addMetadata('org', {
    name: orgName,
    id: orgId,
  });
  event.setUser(userId, userEmail, userName);
};

Bugsnag.start({
  // eslint-disable-next-line no-undef
  appVersion: BUGSNAG_APP_VERSION,
  // eslint-disable-next-line no-undef
  apiKey: BUGSNAG_API_KEY,
  releaseStage: window.location.hostname,
  plugins: [new BugsnagPluginReact()],
  onError: bugSnagErrorCallback,
});

const ErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React);

export const BugsnagClient = Bugsnag;

const ErrorScreen = () => <SomethingWentWrong />;

const render = messages => {
  root.render(
    <Provider store={store}>
      <LanguageProvider messages={messages}>
        <ErrorBoundary FallbackComponent={ErrorScreen}>
          <StyleProvider hashPriority="low">
            <ConfigProvider theme={getCapThemeConfig()}>
              <App />
            </ConfigProvider>
          </StyleProvider>
        </ErrorBoundary>
      </LanguageProvider>
    </Provider>
  );
};

if (module.hot) {
  // Hot reloadable React components and translation json files
  // modules.hot.accept does not accept dynamic dependencies,
  // have to be constants at compile-time
  module.hot.accept(['./i18n', 'components/pages/App'], () => {
    render(translationMessages);
  });
}

// Chunked polyfill for browsers without Intl support
if (!window.Intl) {
  new Promise(resolve => {
    resolve(import('intl'));
  })
    .then(() => Promise.all([import('intl/locale-data/jsonp/en')]))
    .then(() => render(translationMessages))
    .catch(err => {
      throw err;
    });
} else {
  render(translationMessages);
}
